import {
    BusinessUnit,
    DataLayerService,
    FormErrorData,
    FormInteractionType,
    FormName,
    FormStart,
    requiresFormValue
} from '@skydreams/tracking';
import {
    B2bCreateCompanyResponse,
    B2bFormInteractionOptions,
    B2bFormSuccessOptions,
    B2bRegistrationParams,
    TargetingAreaType,
} from '../types/tracking';
import {registerComponent} from '../utils/registry';

export {FormName, FormInteractionType} from '@skydreams/tracking';

export class B2bGATracker {
    private static formIdDataAttributeName = 'data-b2b-form-authcode';

    public static sendFormStartEvent(formName: FormName): Promise<void> {
        let formId = this.getFormId();

        return DataLayerService.sendFormStartEventAsync({
            formId,
            formName,
        } satisfies FormStart);
    }

    public static listenToB2BRegistrationCTAClick(): void {
        /* Exclude the registration form itself (URL contains "/companies/registration")
        so we don't fire the event when the user clicks the back button. */
        if (window.location.href.includes('/companies/registration')) {
            return;
        }

        const registrationLinks = document.querySelectorAll('a[href*="/companies/registration"]');

        registrationLinks.forEach(link => {
            let fired = false;
            const prefire = (): void => {
                if (fired) {
                    return;
                }
                fired = true;
                this.sendFormStartEvent(FormName.BusinessRegistration)
                    .catch(error => console.error(`${FormName.BusinessRegistration as string} form start event failed:`, error));
            };

            /* Prefire on pointerdown so GTM gets a head start before the click navigates away;
            click remains as a fallback for keyboard/Enter activation, and `fired` dedupes the pair. */
            link.addEventListener('pointerdown', prefire);
            link.addEventListener('click', prefire);
        });
    }

    public static sendRegistrationFormSuccessEvent(params: B2bRegistrationParams, createCompanyResponse: B2bCreateCompanyResponse,
                                                   isAVO = false): void {
        const eventData = this.mapEventDataForCompanyResponse(params, createCompanyResponse, isAVO);
        DataLayerService.sendFormSuccessEvent(eventData);
    }

    private static mapEventDataForCompanyResponse(
        params: B2bRegistrationParams,
        createCompanyResponse: B2bCreateCompanyResponse,
        isAVO: boolean
    ): B2bFormSuccessOptions {
        const formId = this.getFormId();

        if (formId === undefined) {
            this.warnUndefinedFormId();
        }

        let dataLayerObject: B2bFormSuccessOptions = {
            formName: FormName.BusinessRegistration,
            eidSha256: createCompanyResponse.emailHash,
            formId: formId,
            company: {
                businessType: params.business_type,
                companyId: createCompanyResponse.companyId,
                companyName: params.companyname,
                contactEmail: params.contact_email,
                contactTelephone: params.contact_telephone,
                contactWebsite: params.contact_website,
                productGroup: createCompanyResponse.categories,
            },
            businessUnit: params.businessUnit,
            businessCategoryId: createCompanyResponse.categoryId,
            companyAddress: {
                countryIso: createCompanyResponse.address.country_iso,
            },
            leadDetails: {
                targetingArea: {
                    type: params.area_tool as TargetingAreaType,
                    unitSystem: createCompanyResponse.unit_system,
                }
            }
        };

        const stepsConfig = isAVO
            ? this.getAVOStepsConfig()
            : this.getStepsConfig({
                formStep: params.formStep,
                formTotalSteps: params.formTotalSteps,
                formStepName: params.formStepName,
            });

        dataLayerObject = {...dataLayerObject, ...stepsConfig};

        if (params.area_tool === TargetingAreaType.Radius) {
            dataLayerObject.leadDetails.targetingArea.radiusValue = params.radius;
            dataLayerObject.leadDetails.targetingArea.radiusLocation = params.location.label;
        }

        for (const key in params) {
            if (key.startsWith('address_')) {
                dataLayerObject.companyAddress[key] = params[key] as string;
            }
        }

        if (params.businessUnit === BusinessUnit.Move) {
            dataLayerObject.leadDetails.removalsInterest = createCompanyResponse.includedTypes.movers;
        }

        return dataLayerObject;
    }

    private static getFormId(): string | undefined {
        const element = document.querySelector(`[${this.formIdDataAttributeName}]`);
        return element?.getAttribute(this.formIdDataAttributeName) ?? undefined;
    }

    private static warnUndefinedFormId(): void {
        console.warn(`B2bGATracker formId is undefined.`);
    }

    private static formDataToObject(data: FormData): Record<string, FormDataEntryValue | FormDataEntryValue[]> {
        const object: Record<string, FormDataEntryValue | FormDataEntryValue[]> = {};

        for (const [key, value] of data.entries()) {
            if (key.includes('_token') || key.includes('__dynamic_error') || value === "") {
                continue;
            }

            const match = key.match(/^[^[]+\[([^\]]+)](.*)$/);
            const modifiedKey = match ? match[1] + match[2] : key;

            if (modifiedKey === '__proto__' || modifiedKey === 'constructor' || modifiedKey === 'prototype') {
                continue;
            }

            if (modifiedKey in object) {
                // If the key already exists, we need to ensure it's an array
                const currentValue = object[modifiedKey];

                if (Array.isArray(currentValue)) {
                    // If it's already an array, just push the new value
                    currentValue.push(value);
                } else {
                    // If it's a single value, convert it to an array containing both
                    object[modifiedKey] = [currentValue, value];
                }
            } else {
                // First time seeing this key, just assign the value
                object[modifiedKey] = value;
            }
        }
        return object;
    }

    public static sendFormInteractionEvent(interactionType: FormInteractionType,
                                           data: FormData | Record<string, unknown> = undefined,
                                           isAVO = false): void {
        try {
            if (requiresFormValue(interactionType) && !data) {
                console.warn(`B2bGATracker: data is required for ${interactionType} form interaction event`);
                return;
            }

            let formDataObject: Record<string, unknown>;
            if (data instanceof FormData) {
                formDataObject = this.formDataToObject(data);
            } else {
                formDataObject = data;
            }

            const gaFormInteractionData = this.getFormInteractionDataLayerObject(interactionType, formDataObject, isAVO);

            if (requiresFormValue(interactionType) && formDataObject) {
                gaFormInteractionData.formValue = formDataObject as Record<string, unknown>;
            }

            if (FormInteractionType.ERROR === interactionType && formDataObject?.errorData) {
                gaFormInteractionData.errorData = formDataObject.errorData as FormErrorData[];
            }

            DataLayerService.sendFormInteractionEvent(gaFormInteractionData);
        } catch (e) {
            console.error('Error sending form interaction event: ', e);
        }
    }

    public static sendProspectFormInteractionEvent(
        interactionType: FormInteractionType,
        options: { businessCategoryId?: number, errorData?: FormErrorData[] } = {}
    ): void {
        try {
            const event: B2bFormInteractionOptions = {
                formName: FormName.ProspectForm,
                formInteraction: interactionType,
                formId: this.getFormId(),
                formStep: '1',
                formTotalSteps: '1',
                formStepName: 'prospect_form',
            };

            if (options.businessCategoryId !== undefined) {
                event.businessCategoryId = options.businessCategoryId;
            }

            if (interactionType === FormInteractionType.ERROR && options.errorData) {
                event.errorData = options.errorData;
            }

            DataLayerService.sendFormInteractionEvent(event);
        } catch (e) {
            console.error('Error sending prospect form interaction event: ', e);
        }
    }

    private static getFormInteractionDataLayerObject(
        formInteraction: FormInteractionType,
        data: FormData | Record<string, unknown> = undefined,
        isAVO = false,
        companyId?: number
    ): B2bFormInteractionOptions {
        const formId = this.getFormId();

        if (formId === undefined) {
            this.warnUndefinedFormId();
        }

        const {
            formStep,
            formTotalSteps,
            formStepName
        } = isAVO ? this.getAVOStepsConfig() : this.getStepsConfig(data as Record<string, unknown>);

        return {
            formId,
            formName: FormName.BusinessRegistration,
            formInteraction,
            formStep,
            formTotalSteps,
            formStepName,
            ...(companyId !== undefined && {
                company: { companyId }
            })
        };
    }

    private static getStepsConfig(
        data: Record<string, unknown> = {}
    ): {formStep: number, formStepName: string, formTotalSteps: number} {
        const safeData = data ?? {};

        return {
            formStep: safeData.formStep as number ?? 0,
            formTotalSteps: safeData.formTotalSteps as number ?? 0,
            formStepName: safeData.formStepName as string ?? '',
        };
    }

    private static getAVOStepsConfig(): {formStep: number, formStepName: string, formTotalSteps: number} {
        const AVOStepsMap = {
            'lead-types': {
                stepName: 'lead-types',
                stepNumber: 1,
            },
            'working-areas': {
                stepName: 'working-areas',
                stepNumber: 2,
            },
            'company-number': {
                stepName: 'company-number',
                stepNumber: 3,
            },
            'company-details': {
                stepName: 'company-details',
                stepNumber: 4,
            },
            'company-address': {
                stepName: 'company-address',
                stepNumber: 5,
            },
            'contact-person': {
                stepName: 'contact-person',
                stepNumber: 6,
            },
            'loader': {
                stepName: 'loader',
                stepNumber: 7,
            }
        };

        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const currentStepName = pathSegments[pathSegments.length - 1] || '';

        if (!AVOStepsMap[currentStepName]) {
            console.error(`B2bGATracker: Unknown AVO step "${currentStepName}"`);
            return { formStep: 0, formTotalSteps: Object.keys(AVOStepsMap).length, formStepName: currentStepName };
        }

        return {
            formStep: AVOStepsMap[currentStepName].stepNumber,
            formTotalSteps: Object.keys(AVOStepsMap).length,
            formStepName: currentStepName,
        };
    }
}

registerComponent('B2bGATracker', B2bGATracker);
window.addEventListener('DOMContentLoaded', () => {
    B2bGATracker.listenToB2BRegistrationCTAClick();
});
